import CreateIcon from "@material-ui/icons/CreateTwoTone";
import PlayListAddCheck from "@material-ui/icons/PlaylistAddCheck";
import useTopElements from "backend/hooks/model/useTopElements";
import useMyTasks from "backend/hooks/workflows/useMyTasks";
import {
  EscalateInfo,
  ICardElementWithChildren,
  IWFState,
} from "backend/model/model";
import WorkflowPagination from "components/WorkflowPagination";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import DelegateModalPopup from "components/workflow/DelegateModalPopup";
import EscalateModalPopup from "components/workflow/EscalateModalPopup";
import WorkflowTypeChip from "design/components/chips/WorkflowTypeChip";
import GenericModal from "design/components/popups/GenericModal";
import MyTasksMenuBtn from "design/components/popups/MyTasksMenuBtn";
import MyTasksTable from "design/components/tables/MytasksTable";
import PageContainer from "design/layouts/PageContainer";
import MyTasksContainer from "design/pages/MyTasksContainer";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useCurrentUser from "qpr-base/context/UserContext";
import useLink from "qpr-base/hooks/useLink";
import AlertMessage from "qpr-base/ui/components/AlertMessage";
import React from "react";

const MyTasks = () => {
  const link = useLink();
  const ctx = useAppContext();
  const [page, setPage] = React.useState(0);
  const ref = React.useRef<any>();
  const {
    pagination: usePaging,
    rowsPerPage,
    showOnlyCurrentTasks,
  } = conf.getMyTasksViewSettings();
  React.useEffect(() => {
    if (page > 0)
      setTimeout(() => {
        if (ref?.current) ref.current.scrollIntoView(false);
      }, 700);
  }, [page]);

  const { data: myTasks, isLoading: isMyTasksLoading } = useMyTasks(
    showOnlyCurrentTasks ? "current" : "coming",
    "mytasks",
    usePaging ? page || 1 : undefined,
    usePaging ? rowsPerPage : undefined
  );
  const { data: topElements, isLoading: topElementsLoading } = useTopElements();
  const [openDelegateModal, setopenDelegateModal] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const [openEscalateModal, setopenEscalateModal] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const [openEscalateInfo, setOpenEscalateInfo] = React.useState<
    EscalateInfo | undefined
  >();
  const user = useCurrentUser();

  const sortedMyTasks = React.useMemo(() => {
    return myTasks || [];
  }, [myTasks]);

  const selectElement = React.useCallback(
    (item: ICardElementWithChildren) => {
      if (item.workflowState?.wfShortType === "value") {
        link({
          page: "workflowinput",
          meaId: item.workflowState?.wfInstanceId,
        });
      }
      if (item.workflowState?.wfShortType === "create") {
        link({
          page:
            item.workflowState?.measureType === "KPI"
              ? "kpisform"
              : "projectsform",
          wf: item.workflowState?.wfInstanceId,
        });
      }
      if (item.workflowState?.wfShortType === "delete") {
        link({
          page:
            item.workflowState?.measureType === "KPI"
              ? "kpisform"
              : "projectsform",
          wf: item.workflowState?.wfInstanceId,
          type: "del",
        });
      }
      if (item.workflowState?.wfShortType === "modify") {
        link({
          page:
            item.workflowState?.measureType === "KPI"
              ? "kpisform"
              : "projectsform",
          wf: item.workflowState?.wfInstanceId,
          type: "new",
        });
      }
    },
    [myTasks]
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const getTaskCount = (): number => {
    if (!myTasks?.length) return 0;
    return myTasks[0].workflowState.totalTaskCount || 0;
  };

  const getSeverity = (
    wf?: IWFState
  ): "error" | "info" | "warning" | "success" => {
    if (wf?.escalateInfo) return "error";
    if (
      wf?.shortState === "dataentry" ||
      wf?.shortState === "update" ||
      wf!.shortState === "approvalrejected"
    )
      return "info";
    return "success";
  };

  const getIcon = (wf?: IWFState): React.ReactNode => {
    return wf?.level === 1 ? <CreateIcon /> : <PlayListAddCheck />;
  };

  const getType = (wf?: IWFState): React.ReactNode => {
    return (
      <WorkflowTypeChip
        elementType={t("configtypes." + wf?.measureType) || "undefined"}
        workflowType={t(wf?.wfShortType || "undefined")}
      />
    );
  };

  if (!topElements || topElementsLoading) return null;

  return (
    <Page isLoading={isMyTasksLoading || !myTasks} Toolbar={WorkflowToolbar}>
      <EscalateModalPopup
        wfState={openEscalateModal?.workflowState}
        onClose={() => {
          setopenEscalateModal(undefined);
        }}
        isOpen={!!openEscalateModal}
        elem={openEscalateModal}
      />
      <DelegateModalPopup
        onClose={() => {
          setopenDelegateModal(undefined);
        }}
        isOpen={!!openDelegateModal}
        elem={openDelegateModal}
      />
      <GenericModal
        open={!!openEscalateInfo}
        onClose={() => {
          setOpenEscalateInfo(undefined);
        }}
      >
        <div>{openEscalateInfo?.message}</div>
        <div>
          <i>{openEscalateInfo?.senderFullname}</i>
        </div>
      </GenericModal>
      <MyTasksContainer>
        <PageContainer>
          <MyTasksTable
            items={sortedMyTasks || []}
            columns={[
              {
                onRowClick: selectElement,
                title: () => t("mytasks.Type"),
                value: ({ elem }) => <div>{getType(elem?.workflowState)}</div>,
              },
              {
                title: t("mytasks.Workflow Status"),
                sortFn: (a, b) =>
                  a.workflowState?.state?.localeCompare(
                    b.workflowState?.state || "undefined"
                  ) || -1,
                onRowClick: selectElement,
                value: ({ elem }) => (
                  <div>
                    <div
                      onClick={(e) => {
                        if (elem.workflowState?.escalateInfo) {
                          setOpenEscalateInfo(elem.workflowState?.escalateInfo);
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <AlertMessage
                        severity={getSeverity(elem.workflowState)}
                        icon={getIcon(elem.workflowState)}
                      >
                        {t(
                          "workflowinput.Alert." + elem.workflowState?.state
                        ) || ""}
                      </AlertMessage>
                    </div>
                  </div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Entity"),
                value: ({ elem }) => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>{elem.workflowState?.measurename}</div>
                    <div>
                      <i>
                        {getTranslatedAttribute(
                          topElements.find(
                            (topElement) =>
                              topElement.name === elem.workflowState?.entity
                          ),
                          "Name",
                          ctx.language
                        )}
                      </i>
                    </div>
                  </div>
                ),
              },
              {
                title: t("mytasks.Period"),
                sortFn: (a, b) =>
                  a.workflowState?.period?.localeCompare(
                    b.workflowState?.period || "undefined"
                  ) || -1,
                onRowClick: selectElement,
                value: ({ elem }) => (
                  <div>{elem.workflowState?.period! || "-"}</div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Received From"),
                value: ({ elem }) => (
                  <div>{elem.workflowState?.receivedfrom ?? "-"}</div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Responsible"),
                value: ({ elem }) => (
                  <div>{elem.workflowState?.responsible ?? "-"}</div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Request Date"),
                value: ({ elem }) => (
                  <div>
                    {elem.workflowState && elem.workflowState.startdate
                      ? new Date(
                          elem.workflowState!.startdate
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                ),
              },
              {
                onRowClick: selectElement,
                title: t("mytasks.Due Date"),
                value: ({ elem }) => (
                  <div>
                    {elem.workflowState && elem.workflowState.duedate
                      ? new Date(
                          elem.workflowState!.duedate
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                ),
              },

              {
                title: t("mytasks.Actions"),
                value: ({ elem }) => (
                  <>
                    <MyTasksMenuBtn
                      items={[
                        { name: "delegate", title: t("mytasks.Delegate") },
                        ...(elem.workflowState?.responsible !== user.fullname
                          ? [{ name: "escalate", title: t("mytasks.Escalate") }]
                          : []),
                      ]}
                      onSelected={(item) => {
                        if (item.name === "delegate") {
                          setopenDelegateModal(elem);
                        }
                        if (item.name === "escalate") {
                          setopenEscalateModal(elem);
                        }
                      }}
                    />
                  </>
                ),
              },
            ]}
          />
          {usePaging && getTaskCount() > (rowsPerPage || 30) && (
            <WorkflowPagination
              ref={ref}
              page={page || 1}
              caption="tasks"
              rowsPerPage={rowsPerPage || 30}
              totalItemCount={getTaskCount()}
              onChange={handlePageChange}
            />
          )}
        </PageContainer>
      </MyTasksContainer>
    </Page>
  );
};

export default MyTasks;
